import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LazyMotion, m, domAnimation } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';
import { useSeo } from '../hooks/useSeo';

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

const Home = () => {
  const { t } = useTranslation();
  const { headerColor, linkStrongColor, iconColor } = useTheme();
  useSeo('home.hero.title', 'home.hero.subtitle');

  return (
    <LazyMotion features={domAnimation}>
    <div className="min-h-screen overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-[80vh] pt-20 pb-28 flex items-center justify-center overflow-hidden text-white" style={{ backgroundColor: '#04263a' }}>
        <div
          aria-hidden="true"
          className="absolute inset-0 z-0"
          style={{
            backgroundImage:
              'radial-gradient(60% 70% at 85% 10%, rgba(231,126,58,0.28), transparent 60%), radial-gradient(70% 80% at 10% 100%, rgba(5,64,98,0.95), transparent 70%), radial-gradient(rgba(255,255,255,0.07) 1px, transparent 1px)',
            backgroundSize: 'auto, auto, 28px 28px'
          }}
        />

        <div className="container relative z-10 text-center">
          <div>
            <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold mb-6 tracking-tight text-balance">
              {t('home.hero.title')}
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto mb-10">
              {t('home.hero.subtitle')}
            </p>

            <div className="flex flex-col md:flex-row items-center justify-center gap-6 mb-12">
              <Link to="/contact" className="btn btn-secondary text-xl px-10 py-4 shadow-xl hover:scale-105 transition-transform">
                {t('home.hero.cta')}
              </Link>
            </div>

            {/* Trust Badges */}
            <m.div
              variants={staggerContainer}
              initial="initial"
              animate="animate"
              className="flex flex-wrap items-center justify-center gap-8 md:gap-16 opacity-80"
            >
              <m.div variants={fadeInUp} className="flex items-center gap-2">
                <svg className="w-6 h-6 text-green-400" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                <span className="font-medium">{t('home.hero.trust.experience')}</span>
              </m.div>
              <m.div variants={fadeInUp} className="flex items-center gap-2">
                <svg className="w-6 h-6 text-green-400" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                <span className="font-medium">{t('home.hero.trust.agile')}</span>
              </m.div>
              <m.div variants={fadeInUp} className="flex items-center gap-2">
                <svg className="w-6 h-6 text-green-400" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                <span className="font-medium">{t('home.hero.trust.quality')}</span>
              </m.div>
            </m.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-white relative">
        <div className="container">
          <m.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">{t('home.services.title')}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">{t('home.services.subtitle')}</p>
          </m.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { key: 'webDev', icon: 'M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' },
              { key: 'mobileDev', icon: 'M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z' },
              { key: 'cloudSolutions', icon: 'M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z' },
              { key: 'consultancy', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2' }
            ].map((service, index) => (
              <m.div
                key={service.key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gray-50 p-8 rounded-2xl shadow-sm hover:shadow-xl transition-all hover:-translate-y-2 border border-gray-100"
              >
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: `${iconColor}20` }}>
                  <svg className="w-8 h-8" style={{ color: iconColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={service.icon}></path>
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-3">{t(`home.services.${service.key}`)}</h3>
                <p className="text-gray-600">
                  {t(`home.services.${service.key}Description`)}
                </p>
              </m.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-24 bg-gray-50">
        <div className="container">
          <m.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-bold mb-4">{t('home.whyUs.title')}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">{t('home.whyUs.subtitle')}</p>
          </m.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {[
              { key: 'expertise', icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z' },
              { key: 'quality', icon: 'M5 13l4 4L19 7' },
              { key: 'support', icon: 'M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z' },
              { key: 'innovation', icon: 'M13 10V3L4 14h7v7l9-11h-7z' }
            ].map((item, index) => (
              <m.div
                key={item.key}
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="flex space-x-6 p-6 bg-white rounded-2xl shadow-sm"
              >
                <div className="flex-shrink-0">
                  <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ backgroundColor: `${iconColor}20` }}>
                    <svg className="w-7 h-7" style={{ color: iconColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={item.icon}></path>
                    </svg>
                  </div>
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-2">{t(`home.whyUs.${item.key}`)}</h3>
                  <p className="text-gray-600 text-lg">
                    {t(`home.whyUs.${item.key}Description`)}
                  </p>
                </div>
              </m.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Products Section */}
      <section className="py-24 bg-white">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">{t('home.products.title')}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">{t('home.products.subtitle')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Detailed Products (Veterinary) */}
            {[
              { key: 'vetApp', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' }
            ].map((product) => (
              <m.div
                key={product.key}
                whileHover={{ y: -5 }}
                className="bg-gray-50 p-8 rounded-3xl shadow-lg border border-gray-100 flex flex-col h-full"
              >
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center mr-5" style={{ backgroundColor: `${iconColor}20` }}>
                    <svg className="w-8 h-8" style={{ color: iconColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={product.icon}></path>
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold">{t(`home.products.${product.key}.title`)}</h3>
                </div>
                <p className="text-gray-600 text-lg mb-6 flex-grow">
                  {t(`home.products.${product.key}.description`)}
                </p>
                <div className="space-y-3 mb-8">
                  {[1, 2].map((f) => (
                    <div key={f} className="flex items-start">
                      <svg className="w-6 h-6 mr-3 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                      <span className="text-gray-700 font-medium">{t(`home.products.${product.key}.feature${f}`)}</span>
                    </div>
                  ))}
                </div>
                <Link to="/products" className="font-bold text-lg hover:translate-x-2 transition-transform inline-flex items-center" style={{ color: linkStrongColor }}>
                  {t('home.products.learnMore')}
                  <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                  </svg>
                </Link>
              </m.div>
            ))}

            {/* Coming Soon Products (Hospital, Store & Gated Community) */}
            {[
              { key: 'hospitalApp', icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4' },
              { key: 'storeApp', icon: 'M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z' },
              { key: 'gatedCommunityApp', icon: 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z' }
            ].map((product) => (
              <div
                key={product.key}
                className="bg-gray-100 p-8 rounded-3xl border border-gray-200 relative overflow-hidden"
              >
                <div className="absolute top-4 right-4 bg-yellow-400 text-yellow-900 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                  {t('products.inProgress')}
                </div>
                <div className="flex items-center mb-6">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center mr-5 bg-gray-200">
                    <svg className="w-7 h-7 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={product.icon}></path>
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800">{t(`home.products.${product.key}.title`)}</h3>
                </div>
                <p className="text-gray-600 text-lg mb-6">
                  {t(`home.products.${product.key}.description`)}
                </p>
                <div className="mt-auto">
                  <span className="text-gray-600 font-medium italic">{t('products.inProgress')}...</span>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-16">
            <Link to="/products" className="btn btn-secondary text-lg px-10 py-4 shadow-lg">
              {t('home.products.viewAllProducts')}
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-gray-50">
        <div className="container max-w-4xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold mb-4">{t('home.faq.title')}</h2>
            <p className="text-xl text-gray-600">{t('home.faq.subtitle')}</p>
          </div>
          <div className="space-y-6">
            {['smallProjects', 'technologies', 'timeline'].map((q) => (
              <m.div
                key={q}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100"
              >
                <h3 className="text-xl font-bold mb-3 flex items-center">
                  <span className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center mr-3 text-sm font-black">Q</span>
                  {t(`home.faq.questions.${q}.question`)}
                </h3>
                <p className="text-gray-600 text-lg ml-11">
                  {t(`home.faq.questions.${q}.answer`)}
                </p>
              </m.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 text-white relative overflow-hidden" style={{ backgroundColor: headerColor }}>
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-10"
          style={{ backgroundImage: 'radial-gradient(circle at 50% 50%, white 1px, transparent 1px)', backgroundSize: '40px 40px' }}
        />
        <div className="container relative z-10 text-center">
          <h2 className="text-4xl md:text-6xl font-extrabold mb-8 tracking-tight">{t('home.cta.title')}</h2>
          <p className="text-xl md:text-2xl mb-12 max-w-3xl mx-auto text-white/90 font-medium leading-relaxed">
            {t('home.cta.description')}
          </p>
          <Link to="/contact" className="btn bg-white hover:bg-gray-100 text-gray-900 text-xl px-12 py-5 shadow-2xl transition-all hover:scale-105 active:scale-95">
            {t('header.contact')}
          </Link>
        </div>
      </section>
    </div>
    </LazyMotion>
  );
};

export default Home;
